import type { Ref } from 'vue'
import type {
  ChatMessageDTO,
  ChatMessageResponse,
  ConversationResponse,
  ConversationsListResponse,
} from './types'
import { useMutation, useQuery } from '@tanstack/vue-query'
import { computed } from 'vue'
import { apiClient } from '@/shared/api'

/**
 * AI Chat API
 */

// --- Send Chat Message ---
export function sendChatMessageRequest(data: ChatMessageDTO) {
  return apiClient.post<ChatMessageResponse>('/async/chatai/chat/', data)
}

export function useSendChatMessageMutation() {
  return useMutation({
    mutationFn: sendChatMessageRequest,
  })
}

// --- Fetch Conversation by ID ---
export function fetchConversationRequest(id: number) {
  // NOTE: base URL already includes '/api', so do not prefix here
  return apiClient.get<ConversationResponse>(`/chatai/conversations/${id}`)
}

export function useConversationQuery(id: Ref<number> | number) {
  const idRef = typeof id === 'number' ? computed(() => id) : id

  return useQuery({
    queryKey: computed(() => ['conversation', idRef.value]),
    queryFn: () => fetchConversationRequest(idRef.value),
    enabled: computed(() => !!idRef.value),
  })
}

// --- Fetch Conversations list (history) ---
export function fetchConversationsRequest(page?: number) {
  return apiClient.get<ConversationsListResponse>('/chatai/conversations/', {
    params: page ? { page } : undefined,
  })
}

export function useConversationsQuery(pageRef?: Ref<number | undefined>) {
  return useQuery({
    queryKey: computed(() => ['conversations', pageRef?.value ?? 1]),
    queryFn: () => fetchConversationsRequest(pageRef?.value),
  })
}

// --- Delete Conversation ---
export function deleteConversationRequest(id: number) {
  return apiClient.delete(`/chatai/conversations/${id}/`)
}

export function useDeleteConversationMutation() {
  return useMutation({
    mutationFn: deleteConversationRequest,
    onSuccess: () => {
      // Invalidate conversations list to refresh the sidebar
      // This will be handled by the component calling refetch()
    },
  })
}

// --- Update Conversation Title ---
export function updateConversationTitleRequest(id: number, title: string) {
  return apiClient.patch(`/chatai/conversations/${id}/`, { title })
}

export function useUpdateConversationTitleMutation() {
  return useMutation({
    mutationFn: ({ id, title }: { id: number, title: string }) =>
      updateConversationTitleRequest(id, title),
    onSuccess: () => {
      // Invalidate conversations list to refresh the sidebar
      // This will be handled by the component calling refetch()
    },
  })
}
